import React, { useState, useEffect } from "react";
import { Modal, Upload, Spin, message, Button } from "antd";
import { InboxOutlined, FileOutlined } from "@ant-design/icons";
import { postAuthMethod } from "../../services/HttpServices";
import { useAlert } from "../../context/AlertProvider";

const { Dragger } = Upload;

const getAcceptString = (requiredType) => {
  if (!requiredType) return undefined;
  const type = requiredType.toUpperCase();
  if (type === "IMAGE") {
    return "image/*,image/jpeg,image/png,image/jpg,image/webp,image/gif";
  }
  if (type === "VIDEO") {
    return "video/*,video/mp4,video/3gpp,video/quicktime,video/avi";
  }
  if (type === "DOCUMENT") {
    return ".pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation";
  }
  return undefined;
};

const MediaPickerModal = ({ visible, onClose, onSelect, requiredType }) => {
  const alertContext = useAlert();
  const showAlert = alertContext?.showAlert;
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  // Clear state when modal is closed or opened
  useEffect(() => {
    if (!visible) {
      handleClear();
    }
  }, [visible]);

  const handleClear = () => {
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  const handleUploadFile = async (fileToUpload) => {
    if (!fileToUpload) return;
    const formData = new FormData();
    formData.append("file", fileToUpload);

    setUploading(true);
    try {
      const res = await postAuthMethod("/api/media", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (showAlert) {
        showAlert("success", "Media selected successfully");
      } else {
        message.success("Media selected successfully");
      }

      if (res.data) {
        onSelect(res.data);
        onClose();
        handleClear();
      }
    } catch (error) {
      const errMsg = error.response?.data?.message || "File upload failed";
      if (showAlert) {
        showAlert("error", errMsg);
      } else {
        message.error(errMsg);
      }
    } finally {
      setUploading(false);
    }
  };

  const beforeUpload = (file) => {
    if (requiredType) {
      const type = requiredType.toUpperCase();
      if (type === "IMAGE" && !file.type.startsWith("image/")) {
        const isImageExt = /\.(jpg|jpeg|png|webp|gif|bmp|svg)$/i.test(file.name);
        if (!isImageExt) {
          const msg = "Please select a valid image file (JPG, PNG, WEBP, etc.)";
          if (showAlert) showAlert("error", msg);
          else message.error(msg);
          return Upload.LIST_IGNORE;
        }
      } else if (type === "VIDEO" && !file.type.startsWith("video/")) {
        const isVideoExt = /\.(mp4|3gp|mov|avi|mkv|webm)$/i.test(file.name);
        if (!isVideoExt) {
          const msg = "Please select a valid video file (MP4, 3GP, etc.)";
          if (showAlert) showAlert("error", msg);
          else message.error(msg);
          return Upload.LIST_IGNORE;
        }
      } else if (type === "DOCUMENT") {
        if (file.type.startsWith("image/") || file.type.startsWith("video/")) {
          const msg = "Please select a document file (PDF, DOC, TXT, etc.)";
          if (showAlert) showAlert("error", msg);
          else message.error(msg);
          return Upload.LIST_IGNORE;
        }
      }
    }

    setSelectedFile(file);
    if (file.type.startsWith("image/")) {
      setPreviewUrl(URL.createObjectURL(file));
    } else {
      setPreviewUrl(null);
    }

    handleUploadFile(file);
    return false; // Prevent auto upload
  };

  const handleUploadClick = () => {
    if (selectedFile) handleUploadFile(selectedFile);
  };

  return (
    <Modal
      title={<span className="tw-text-[18px] tw-font-bold tw-text-[#1E293B]">{`Upload ${requiredType ? requiredType.toLowerCase() : "media"}`}</span>}
      visible={visible}
      open={visible}
      onCancel={() => {
        handleClear();
        onClose();
      }}
      footer={null}
      centered
      styles={{
        content: { borderRadius: "20px", overflow: "hidden", padding: 0 },
        header: { padding: "20px 28px", margin: 0, borderBottom: "1px solid #F1F5F9" },
        body: { padding: "24px 28px" }
      }}
      width={520}
    >
      <div className="tw-py-2">
        {!selectedFile ? (
          <Dragger
            beforeUpload={beforeUpload}
            showUploadList={false}
            accept={getAcceptString(requiredType)}
            disabled={uploading}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Click or drag file to this area to upload
            </p>
            <p className="ant-upload-hint">
              Supports single file upload for {requiredType ? requiredType.toLowerCase() : "media"}.<br />
              <span className="tw-text-gray-400 tw-text-xs">
                If the file was already uploaded previously, it will be instantly reused.
              </span>
            </p>
          </Dragger>
        ) : (
          <div className="tw-flex tw-flex-col tw-items-center tw-gap-4">
            <div className="tw-w-full tw-max-w-xs tw-border tw-border-solid tw-border-slate-200 tw-rounded-2xl tw-p-4 tw-bg-slate-50 tw-flex tw-flex-col tw-items-center">
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="tw-w-full tw-h-auto tw-object-contain tw-rounded-xl"
                  style={{ maxHeight: 200 }}
                />
              ) : (
                <div className="tw-py-6 tw-text-gray-400">
                  <FileOutlined style={{ fontSize: "48px" }} />
                </div>
              )}
              <div
                className="tw-mt-4 tw-text-sm tw-font-semibold tw-text-center tw-truncate tw-w-full"
                title={selectedFile.name}
              >
                {selectedFile.name}
              </div>
              <div className="tw-text-xs tw-text-gray-500 tw-mt-1">
                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
              </div>
            </div>

            <div className="tw-flex tw-justify-end tw-gap-3 tw-w-full tw-mt-4">
              <Button
                onClick={handleClear}
                disabled={uploading}
                className="!tw-h-[38px] !tw-rounded-lg !tw-border-slate-200 !tw-bg-white !tw-px-5 !tw-text-xs !tw-font-bold !tw-text-slate-500 hover:!tw-border-slate-300 hover:!tw-text-slate-700 tw-transition-all tw-duration-200"
              >
                Change File
              </Button>
              <Button
                type="primary"
                onClick={handleUploadClick}
                loading={uploading}
                className="!tw-h-[38px] !tw-rounded-lg !tw-bg-[#5D44FF] !tw-border-[#5D44FF] !tw-text-white tw-text-xs tw-font-bold tw-px-6 tw-shadow-sm hover:tw-shadow-md tw-transition-all tw-duration-200"
              >
                Upload & Select
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default MediaPickerModal;
